import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useQueryClient } from '@tanstack/react-query'
import { useLatestState } from 'src/Libs/flow/advanced-settings/hooks'
import { InstallationConfigDto, updateInstallationConfig, syncInstallation } from 'src/Libs/flow/installation/api'
import { installationConfigKey } from 'src/Libs/flow/installation/hooks'
import { usePortalMutation } from 'src/Libs/api'

interface StorageProps {
  installationId: number
  installationConfig: Nullable<InstallationConfigDto>
}

interface ReturnType {
  solarMode: boolean
  boostMode: boolean
  updateSolarMode: (newSolarMode: boolean) => void
  updateBoostMode: (newBoostMode: boolean) => void
  areMutationsLoading: boolean
}

/** Helper-Hook for moving some functionality/logic for the main-page to an own file  */
export const useStorage = ({ installationId, installationConfig }: StorageProps): ReturnType => {
  const { t } = useCustomTranslation()
  const { enqueueErrorStack } = useSnackbar()
  const authFetch = useAuthFetchWithResult()
  const queryClient = useQueryClient()

  const currentSolarMode = !!installationConfig?.emsBatteryToCarMode
  const [solarMode, setSolarMode] = useLatestState(currentSolarMode)

  const currentBoostMode = !!installationConfig?.emsWallboxEnforcePowerAssignment
  const [boostMode, setBoostMode] = useLatestState(currentBoostMode)

  const mutateSolarMode = usePortalMutation({
    mutationFn: (newSolarMode: boolean) =>
      updateInstallationConfig(
        installationId,
        {
          emsBatteryToCarMode: newSolarMode,
          emsBatteryBeforeCarMode: false,
        },
        authFetch
      ).then(() => syncInstallation(installationId, authFetch)),
    onMutate: value => setSolarMode(value),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: installationConfigKey(installationId),
      }),
    onError: () => {
      enqueueErrorStack(t('common.update-failed-error'))
      setSolarMode(currentSolarMode)
    },
  })

  const updateSolarMode = (newSolarMode: boolean): void => {
    if (newSolarMode !== currentSolarMode) {
      mutateSolarMode.mutate(newSolarMode)
    }
  }

  const mutateBoostMode = usePortalMutation({
    mutationFn: (newBoostMode: boolean) =>
      updateInstallationConfig(
        installationId,
        {
          emsWallboxEnforcePowerAssignment: newBoostMode,
          emsBatteryBeforeCarMode: false,
        },
        authFetch
      ).then(() => syncInstallation(installationId, authFetch)),
    onMutate: value => setBoostMode(value),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: installationConfigKey(installationId),
      }),
    onError: () => {
      enqueueErrorStack(t('common.update-failed-error'))
      setBoostMode(currentBoostMode)
    },
  })

  const updateBoostMode = (newBoostMode: boolean): void => {
    if (newBoostMode !== currentBoostMode) {
      mutateBoostMode.mutate(newBoostMode)
    }
  }

  const areMutationsLoading = mutateSolarMode.isPending || mutateBoostMode.isPending

  return {
    solarMode,
    boostMode,
    updateSolarMode,
    updateBoostMode,
    areMutationsLoading,
  }
}
